import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import './WeddingsEvents.css';
import Navbar from '../components/Navbar';
import TopInfoBar from '../components/TopInfoBar';
import BookingSidebar from '../components/BookingSidebar';
import Footer from '../components/Footer';
import Wedding1 from '../assets/outdoor/Wedding1.jpg';
import Wedding2 from '../assets/outdoor/Wedding2.jpg';
import Wedding3 from '../assets/outdoor/Wedding3.jpg';

/** Set to true to show the Request a Quote form again */
const CONTACT_FORM_ENABLED = false;

const WEDDING_IMAGES = [
  { src: Wedding1, alt: 'Wedding at Hacienda Del Sol', title: 'Elegant Wedding Celebration' },
  { src: Wedding2, alt: 'Wedding at Hacienda Del Sol', title: 'Beautiful Outdoor Ceremony' },
  { src: Wedding3, alt: 'Wedding at Hacienda Del Sol', title: 'Memorable Wedding Reception' },
];

function WeddingsEvents() {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    numberOfGuests: '',
    eventType: '',
    wantsTour: false,
    message: ''
  });

  // Preload all images to prevent black box flash during scrolling
  useEffect(() => {
    const imagePromises = WEDDING_IMAGES.map((image) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = reject;
        img.src = image.src;
      });
    });

    Promise.all(imagePromises).catch((error) => {
      console.error('Error preloading images:', error);
    });
  }, []);

  const handleOpenBooking = () => setIsBookingOpen(true);
  const handleCloseBooking = () => setIsBookingOpen(false);

  const handlePrevious = () => {
    if (isTransitioning) return; // Prevent rapid clicking
    setIsTransitioning(true);
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? WEDDING_IMAGES.length - 1 : prevIndex - 1
    );
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleNext = () => {
    if (isTransitioning) return; // Prevent rapid clicking
    setIsTransitioning(true);
    setCurrentImageIndex((prevIndex) =>
      prevIndex === WEDDING_IMAGES.length - 1 ? 0 : prevIndex + 1
    );
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    // Format the event date for display
    const formattedDate = formData.eventDate 
      ? new Date(formData.eventDate).toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })
      : 'Not specified';

    // Format event type for display
    const eventTypeLabels = {
      wedding: 'Wedding',
      corporate: 'Corporate Event',
      birthday: 'Birthday Celebration',
      anniversary: 'Anniversary',
      other: 'Other'
    };
    const formattedEventType = eventTypeLabels[formData.eventType] || formData.eventType;

    // Get recipient email from environment variable or use default
    // You can set REACT_APP_RECIPIENT_EMAIL in your .env file
    const recipientEmail = process.env.REACT_APP_RECIPIENT_EMAIL || 'aaron.ong.dev@gmail.com';

    // Prepare email template parameters
    // Note: to_email is set in EmailJS template settings, not here
    // Ensure all values are strings to avoid EmailJS corruption errors
    const templateParams = {
      from_name: String(formData.name || ''),
      from_email: String(formData.email || ''),
      phone: String(formData.phone || ''),
      event_date: String(formattedDate || 'Not specified'),
      number_of_guests: String(formData.numberOfGuests || ''),
      event_type: String(formattedEventType || ''),
      wants_tour: String(formData.wantsTour ? 'Yes' : 'No'),
      message: String(formData.message || 'No additional details provided')
    };

    // Log template params for debugging
    console.log('📧 Template parameters being sent:', templateParams);

    try {
      // Send email using EmailJS
      // Set these values in your .env file or replace directly:
      // REACT_APP_EMAILJS_SERVICE_ID=your_service_id
      // REACT_APP_EMAILJS_TEMPLATE_ID=your_template_id
      // REACT_APP_EMAILJS_PUBLIC_KEY=your_public_key
      const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
      const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
      const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

      // Debug: Log what credentials are being used
      console.log('🔍 EmailJS Configuration Check:');
      console.log('  Service ID:', serviceId.substring(0, 10) + '...' + (serviceId === 'YOUR_SERVICE_ID' ? ' ❌ NOT SET' : ' ✅'));
      console.log('  Template ID:', templateId.substring(0, 10) + '...' + (templateId === 'YOUR_TEMPLATE_ID' ? ' ❌ NOT SET' : ' ✅'));
      console.log('  Public Key:', publicKey.substring(0, 10) + '...' + (publicKey === 'YOUR_PUBLIC_KEY' ? ' ❌ NOT SET' : ' ✅'));

      // Check if EmailJS is configured
      const isTestMode = serviceId === 'YOUR_SERVICE_ID' || templateId === 'YOUR_TEMPLATE_ID' || publicKey === 'YOUR_PUBLIC_KEY';
      
      if (isTestMode) {
        // TEST MODE: Simulate success for testing without EmailJS
        console.warn('⚠️ EmailJS not configured - running in TEST MODE');
        console.warn('📧 Email would be sent to:', recipientEmail);
        console.warn('📋 Form data:', templateParams);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        console.log('✅ Test mode: Form submission successful (no email sent)');
      } else {
        // EmailJS is configured, send the email
        console.log('📤 Attempting to send email via EmailJS...');
        console.log('📧 Recipient email:', recipientEmail);
        console.log('🔑 Service ID:', serviceId);
        console.log('📝 Template ID:', templateId);
        console.log('📋 Template Params:', JSON.stringify(templateParams, null, 2));
        
        try {
          const response = await emailjs.send(
            serviceId,
            templateId,
            templateParams,
            publicKey
          );
          
          console.log('✅ EmailJS Response:', response);
          console.log('✅ Email sent successfully! Status:', response.status);
          console.log('✅ Email should arrive at:', recipientEmail);
          console.log('💡 Check your spam folder if you don\'t see it in your inbox');
        } catch (emailError) {
          console.error('❌ EmailJS Send Error:', emailError);
          console.error('❌ Error Details:', {
            message: emailError.message,
            text: emailError.text,
            status: emailError.status
          });
          throw emailError; // Re-throw to be caught by outer catch
        }
      }

      setSubmitStatus('success');
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        eventDate: '',
        numberOfGuests: '',
        eventType: '',
        wantsTour: false,
        message: ''
      });

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('❌ Error sending email:', error);
      console.error('Error details:', {
        message: error.message,
        text: error.text,
        status: error.status
      });
      
      let userFriendlyError = 'There was an error submitting your request. Please try again or contact us directly.';
      
      // Show more helpful error message if EmailJS isn't configured
      if (error.message && error.message.includes('EmailJS is not configured')) {
        userFriendlyError = 'EmailJS is not configured. Please check your .env file or contact the developer.';
        console.error('Please configure EmailJS:');
        console.error('1. Create an account at https://www.emailjs.com/');
        console.error('2. Set up your email service and template');
        console.error('3. Add your credentials to .env file or directly in the code');
      } else if (error.text) {
        // EmailJS API error
        console.error('EmailJS API Error:', error.text);
        if (error.text.includes('Invalid template ID') || error.text.includes('Invalid service ID')) {
          userFriendlyError = 'EmailJS configuration error. Please check your Service ID and Template ID.';
        } else if (error.text.includes('Invalid public key')) {
          userFriendlyError = 'EmailJS configuration error. Please check your Public Key.';
        } else {
          userFriendlyError = `Email error: ${error.text}`;
        }
      } else if (error.message) {
        userFriendlyError = `Error: ${error.message}`;
      }
      
      setSubmitStatus('error');
      setErrorMessage(userFriendlyError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="weddings-events" id="weddings-events">
      <TopInfoBar />
      <Navbar onBookNowClick={handleOpenBooking} />
      <section className="hero">
        <div className="hero-image-wrapper">
          <img
            src={WEDDING_IMAGES[currentImageIndex].src}
            alt={WEDDING_IMAGES[currentImageIndex].alt}
            className={`hero-image ${currentImageIndex === 2 ? 'hero-image--bottom' : ''}`}
            loading="eager"
          />
          <div className="hero-overlay"></div>
          <button
            className="hero-arrow hero-arrow--left"
            onClick={handlePrevious}
            aria-label="Previous image"
            disabled={isTransitioning}
          >
            ‹
          </button>
          <button
            className="hero-arrow hero-arrow--right"
            onClick={handleNext}
            aria-label="Next image"
            disabled={isTransitioning}
          >
            ›
          </button>
          <div className="hero-content">
            <h1 className="hero-title">Weddings & Events</h1>
            <p className="hero-subtitle">{WEDDING_IMAGES[currentImageIndex].title}</p>
            <div className="hero-pagination">
              {currentImageIndex + 1} / {WEDDING_IMAGES.length}
            </div>
          </div>
        </div>
      </section>
      {CONTACT_FORM_ENABLED && (
      <section className="quote-form-section">
        <div className="quote-form-container">
          <div className="quote-form-header">
            <h2 className="quote-form-title">Request a Quote</h2>
            <p className="quote-form-subtitle">Let us help you plan your perfect event</p>
          </div>
          <form className="quote-form" onSubmit={handleSubmit}>
            <div className="quote-form-row">
              <div className="quote-form-group">
                <label htmlFor="name" className="quote-form-label">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="quote-form-input"
                  required
                />
              </div>
              <div className="quote-form-group">
                <label htmlFor="email" className="quote-form-label">Email Address *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="quote-form-input"
                  required
                />
              </div>
            </div>
            <div className="quote-form-row">
              <div className="quote-form-group">
                <label htmlFor="phone" className="quote-form-label">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="quote-form-input"
                  required
                />
              </div>
              <div className="quote-form-group">
                <label htmlFor="eventDate" className="quote-form-label">Event Date *</label>
                <input
                  type="date"
                  id="eventDate"
                  name="eventDate"
                  value={formData.eventDate}
                  onChange={handleInputChange}
                  className="quote-form-input"
                  required
                />
              </div>
            </div>
            <div className="quote-form-row">
              <div className="quote-form-group">
                <label htmlFor="numberOfGuests" className="quote-form-label">Number of Guests *</label>
                <input
                  type="number"
                  id="numberOfGuests"
                  name="numberOfGuests"
                  value={formData.numberOfGuests}
                  onChange={handleInputChange}
                  className="quote-form-input"
                  min="1"
                  required
                />
              </div>
              <div className="quote-form-group">
                <label htmlFor="eventType" className="quote-form-label">Event Type *</label>
                <select
                  id="eventType"
                  name="eventType"
                  value={formData.eventType}
                  onChange={handleInputChange}
                  className="quote-form-input"
                  required
                >
                  <option value="">Select Event Type</option>
                  <option value="wedding">Wedding</option>
                  <option value="corporate">Corporate Event</option>
                  <option value="birthday">Birthday Celebration</option>
                  <option value="anniversary">Anniversary</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="quote-form-group quote-form-checkbox-group">
              <label className="quote-form-checkbox-label">
                <input
                  type="checkbox"
                  name="wantsTour"
                  checked={formData.wantsTour}
                  onChange={handleInputChange}
                  className="quote-form-checkbox"
                />
                <span className="quote-form-checkbox-text">I would like to schedule a tour of the venue</span>
              </label>
            </div>
            <div className="quote-form-group">
              <label htmlFor="message" className="quote-form-label">Additional Details</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                className="quote-form-textarea"
                rows="5"
                placeholder="Tell us about your event vision, special requirements, or any questions you may have..."
              />
            </div>
            {submitStatus === 'success' && (
              <div className="quote-form-message quote-form-message--success">
                Thank you for your request! We will contact you soon.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="quote-form-message quote-form-message--error">
                {errorMessage || 'There was an error submitting your request. Please try again or contact us directly.'}
              </div>
            )}
            <button 
              type="submit" 
              className="quote-form-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Submit Request'}
            </button>
          </form>
        </div>
      </section>
      )}
      <Footer onBookNowClick={handleOpenBooking} />
      <BookingSidebar isOpen={isBookingOpen} onClose={handleCloseBooking} />
    </div>
  );
}

export default WeddingsEvents;

